import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { workExperience, education, accomplishments } from '../data';

function Experience() {
  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 800, color: '#00a2ff', fontFamily: "'Share Tech Mono', monospace" }}>
          [ Security Auditing & Work Journey ]
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto', fontSize: '0.85rem' }}>
          My industry experiences, education background, open-source work, and technical achievements.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Work Experience */}
        <Grid item xs={12} lg={7.5}>
          <Typography variant="body1" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1, color: '#00a2ff', fontFamily: "'Share Tech Mono', monospace" }}>
            <BusinessCenterIcon fontSize="small" /> [ Experience Timeline ]
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, borderLeft: '2px solid rgba(0, 162, 255, 0.15)', pl: 2, ml: 1 }}>
            {workExperience.map((job, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                {/* Timeline Dot */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    left: -25, 
                    top: 6, 
                    width: 10, 
                    height: 10, 
                    borderRadius: '50%', 
                    bgcolor: '#080b11', 
                    border: '2px solid #00a2ff',
                    boxShadow: '0 0 6px rgba(0, 162, 255, 0.8)',
                    zIndex: 2
                  }} 
                />

                <Card 
                  sx={{ 
                    p: 2,
                    border: '1px solid #1c2536',
                    background: '#0d1017',
                    '&:hover': {
                      borderColor: 'rgba(0, 162, 255, 0.3) !important',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                    <Box>
                      <Typography variant="body1" component="h3" sx={{ fontWeight: 800, color: '#ffffff', fontFamily: "'Share Tech Mono', monospace", fontSize: '0.95rem' }}>
                        {job.role}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#00a2ff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {job.company} 
                        <Chip label={job.type} size="small" sx={{ height: 16, bgcolor: 'rgba(0, 162, 255, 0.06)', color: '#33b5ff', fontWeight: 700, border: '1px solid rgba(0, 162, 255, 0.15)', fontSize: '0.62rem', '& .MuiChip-label': { px: 0.5 } }} />
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.02)', px: 1, py: 0.2, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)', fontFamily: "'Share Tech Mono', monospace" }}>
                      {job.duration}
                    </Typography>
                  </Box>

                  <Box component="ul" sx={{ pl: 1.5, color: 'text.secondary', mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    {job.points.map((pt, i) => (
                      <li key={i}>
                        {pt}
                      </li>
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {job.skills.map((skill) => (
                      <Chip 
                        key={skill} 
                        label={skill} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.02)', 
                          color: 'text.secondary', 
                          fontSize: '0.65rem',
                          border: '1px solid rgba(255, 255, 255, 0.04)',
                          height: 16,
                          '& .MuiChip-label': { px: 0.5 }
                        }} 
                      />
                    ))}
                  </Box>
                </Card>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right Column - Education & Accomplishments */}
        <Grid item xs={12} lg={4.5} sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          {/* Education Section */}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#a855f7', fontFamily: "'Share Tech Mono', monospace" }}>
              <SchoolIcon fontSize="small" /> [ Education ]
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {education.map((edu, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    p: 2,
                    border: '1px solid #1c2536',
                    background: '#0d1017',
                    '&:hover': {
                      borderColor: 'rgba(168, 85, 247, 0.3) !important',
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#a855f7', fontWeight: 700, mb: 0.5, display: 'block', fontFamily: "'Share Tech Mono', monospace" }}>
                    {edu.duration}
                  </Typography>
                  <Typography variant="body2" component="h3" sx={{ fontWeight: 800, color: '#ffffff', mb: 0.5, fontSize: '0.88rem', lineHeight: 1.2, fontFamily: "'Share Tech Mono', monospace" }}>
                    {edu.degree}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                    {edu.school}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.3 }}>
                    {edu.details}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Accomplishments Section */}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444', fontFamily: "'Share Tech Mono', monospace" }}>
              <EmojiEventsIcon fontSize="small" /> [ Achievements ]
            </Typography>

            <Card sx={{ p: 2, border: '1px solid #1c2536', background: '#0d1017' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {accomplishments.map((acc, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ef4444', mt: 0.8, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.4 }}>
                      {acc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Experience;
